import { AddressComponents } from "@/components/location/location-selector";
import { ItemObj, OrderDetails } from "@/components/modals/order-details-sheet";
import { identity } from "rxjs";

export type LineItem = {
  itemId: string;
  quantity: number;
  [key: string]: unknown;
};

export type Cart = {
  orderId: string;
  orderVersion: number;
  locationId: string;
  lineItems: ItemObj[];
  cartID?: string | null;
  total?: {
    amount: number;
    currency: string;
  };
};

export type Recipient = {
  displayName: string;
  emailAddress: string;
  phoneNumber: string;
  address: AddressComponents | null;
}

export type OrderServeDetails = {
  fullFillType: string;
  fulfillmentState: string;
  deliveryDetails: {
    recipient: Recipient;
    scheduleType: string;
    note: string;
    deliveryAt?: string | null; // Optional, only if scheduleType is SCHEDULED
    prepTimeDuration: string | null;
  } | null;
  pickupDetails: {
    recipient: Recipient;
    pickupAt: string | null;
    scheduleType: string;
    note: string;
    prepTime: string | null;
  } | null;
  setAt: Date; // Use string or Date based on your preference
};
type PreviousOrder = OrderDetails;

const initialState = {
  cart: {
    orderId: "",
    orderVersion: 0,
    locationId: "",
    lineItems: [],
  } as Cart,
};

export const createOrderActionsSlice = (
  set: (fn: (state: OrderActionsState) => Partial<OrderActionsState> | Partial<OrderActionsState>) => void,
  get: () => OrderActionsState
) => ({
  ...initialState,
  addToCart: (saveObj: Cart) => set((state: OrderActionsState) => {
    // console.log("Adding item to cart:", saveObj);

    return {
      cart: saveObj,
      cartID: state.cart.cartID || identity("cart_" + new Date().getTime())
    };
  }),
  updateCartItem: (item: LineItem, quantity: number) => set((state: OrderActionsState) => {
    const updatedCart = { ...state.cart };
    const existingItemIndex = updatedCart.lineItems.findIndex((i: ItemObj) => i.itemId === item.itemId);

    if (existingItemIndex > -1) {
      updatedCart.lineItems[existingItemIndex].quantity = quantity;
    }

    return { cart: updatedCart };
  }),
  removeFromCart: (itemId: string) => set((state: OrderActionsState) => {
    const updatedCart = { ...state.cart };
    updatedCart.lineItems = updatedCart.lineItems.filter((item: ItemObj) => item.itemId !== itemId);
    if (updatedCart.lineItems.length === 0) {
      updatedCart.orderId = "";
      updatedCart.orderVersion = 0;
      updatedCart.locationId = "";
      updatedCart.lineItems = [];
    }
    return { cart: updatedCart };
  }),
  clearCart: () => set(() => (initialState)),
  getCart: () => get().cart || {},

  selectedItem: null as unknown,
  setSelectedItem: (item: unknown) => set(() => ({ selectedItem: item })),
  getSelectedItem: () => get().selectedItem || null,
  clearSelectedItem: () => set(() => ({ selectedItem: null })),

  orderServeDetails: null as OrderServeDetails | null,
  setOrderServeDetails: (details: OrderServeDetails | null) => set(() => ({ orderServeDetails: details })),
  getOrderServeDetails: () => get().orderServeDetails || null,
  clearOrderServeDetails: () => set(() => ({ orderServeDetails: null })),

  previousOrders: [] as PreviousOrder[],
  setPreviousOrders: (orders: PreviousOrder[]) => set(() => ({ previousOrders: orders })),
  getPreviousOrders: () => get().previousOrders || [],
  clearPreviousOrders: () => set(() => ({ previousOrders: [] })),

  previousDeliveryAddresses: [] as AddressComponents[],
  setPreviousDeliveryAddresses: (addresses: AddressComponents[]) => set(() => ({ previousDeliveryAddresses: addresses })),
  getPreviousDeliveryAddresses: () => get().previousDeliveryAddresses || [],
  clearPreviousDeliveryAddresses: () => set(() => ({ previousDeliveryAddresses: [] })),
});

export type OrderActionsState = {
  cart: Cart;
  addToCart: (saveObj: Cart) => void;
  updateCartItem: (item: LineItem, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  getCart: () => Cart;
  selectedItem: unknown;
  setSelectedItem: (item: unknown) => void;
  getSelectedItem: () => unknown;
  clearSelectedItem: () => void;
  orderServeDetails: OrderServeDetails | null;
  setOrderServeDetails: (details: OrderServeDetails | null) => void;
  getOrderServeDetails: () => OrderServeDetails | null;
  clearOrderServeDetails: () => void;
  previousOrders: PreviousOrder[];
  setPreviousOrders: (orders: PreviousOrder[]) => void;
  getPreviousOrders: () => PreviousOrder[];
  clearPreviousOrders: () => void;
  previousDeliveryAddresses: AddressComponents[];
  setPreviousDeliveryAddresses: (addresses: AddressComponents[]) => void;
  getPreviousDeliveryAddresses: () => AddressComponents[];
  clearPreviousDeliveryAddresses: () => void;
};
